import { lazy } from 'react';
import { createHashRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/app/layout/AppShell';
import { RouteFehler } from '@/app/RouteFehler';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { ZumLebensrad } from '@/modules/lifewheel/ZumLebensrad';

/**
 * HASH-ROUTING, NICHT BROWSER-ROUTING.
 *
 * Die App laeuft auf einem reinen Dateiserver ohne SPA-Rewrite. Mit
 * Hash-Routing (`/#/kalender`) sieht der Server nie einen Routenpfad, und
 * Lesezeichen, Neuladen und Deep Links funktionieren ohne Umwege.
 *
 * Cloudflare Pages KOENNTE per `_redirects` ein Fallback liefern - das ist
 * trotzdem kein Grund zu wechseln: alle bestehenden Lesezeichen enthalten das
 * `#`, ein Wechsel bricht sie. Nicht "aufraeumen" zu `createBrowserRouter` -
 * siehe docs/adr/0002.
 *
 * NACHLADEN JE ROUTE: alles ausser der Startseite wird erst beim Aufruf
 * geladen. Die Startseite bleibt fest im Hauptbuendel, weil sie beim Start
 * ohnehin sofort gebraucht wird - sie nachzuladen hiesse, beim Oeffnen der App
 * zweimal zu warten.
 */
const lade = {
  areas: lazy(async () => ({ default: (await import('@/modules/areas/AreasPage')).AreasPage })),
  settings: lazy(async () => ({ default: (await import('@/modules/settings/SettingsPage')).SettingsPage })),
  tagesvorlagen: lazy(async () => ({ default: (await import('@/modules/tagesvorlagen/VorlagenPage')).VorlagenPage })),
  calendar: lazy(async () => ({ default: (await import('@/modules/calendar/CalendarPage')).CalendarPage })),
  tasks: lazy(async () => ({ default: (await import('@/modules/tasks/TasksPage')).TasksPage })),
  habits: lazy(async () => ({ default: (await import('@/modules/habits/HabitsPage')).HabitsPage })),
  goals: lazy(async () => ({ default: (await import('@/modules/goals/GoalsPage')).GoalsPage })),
  projects: lazy(async () => ({ default: (await import('@/modules/projects/ProjectsPage')).ProjectsPage })),
  project: lazy(async () => ({ default: (await import('@/modules/projects/ProjectDetailPage')).ProjectDetailPage })),
  buchungenSeite: lazy(async () => ({ default: (await import('@/modules/finanzen/FinanzSeiten')).BuchungenSeite })),
  sparzielSeite: lazy(async () => ({ default: (await import('@/modules/finanzen/FinanzSeiten')).SparzielSeite })),
  auswertungSeite: lazy(async () => ({ default: (await import('@/modules/finanzen/AuswertungSeite')).AuswertungSeite })),
  budgetSeite: lazy(async () => ({ default: (await import('@/modules/finanzen/FinanzSeiten')).BudgetSeite })),
  abosSeite: lazy(async () => ({ default: (await import('@/modules/finanzen/FinanzSeiten')).AbosSeite })),
  stundenplanSeite: lazy(async () => ({ default: (await import('@/modules/school/components/SchuleSeiten')).StundenplanSeite })),
  faecherSeite: lazy(async () => ({ default: (await import('@/modules/school/components/SchuleSeiten')).FaecherSeite })),
  notenSeite: lazy(async () => ({ default: (await import('@/modules/school/components/SchuleSeiten')).NotenSeite })),
  klausurenSeite: lazy(async () => ({ default: (await import('@/modules/school/components/SchuleSeiten')).KlausurenSeite })),
  mitschriftenSeite: lazy(async () => ({ default: (await import('@/modules/school/components/SchuleSeiten')).MitschriftenSeite })),
  subject: lazy(async () => ({ default: (await import('@/modules/school/SubjectDetailPage')).SubjectDetailPage })),
  goal: lazy(async () => ({ default: (await import('@/modules/goals/GoalDetailPage')).GoalDetailPage })),
  journal: lazy(async () => ({ default: (await import('@/modules/journal/JournalPage')).JournalPage })),
  review: lazy(async () => ({ default: (await import('@/modules/review/ReviewPage')).ReviewPage })),
  statistik: lazy(async () => ({ default: (await import('@/modules/statistik/StatistikPage')).StatistikPage })),
  resources: lazy(async () => ({ default: (await import('@/modules/resources/ResourcesPage')).ResourcesPage })),
  bereich: lazy(async () => ({ default: (await import('@/modules/areas/board/BereichBoard')).BereichBoard })),
  wissen: lazy(async () => ({ default: (await import('@/modules/wissen/WissenPage')).WissenPage })),
  listen: lazy(async () => ({ default: (await import('@/modules/listen/ListenPage')).ListenPage })),
  liste: lazy(async () => ({ default: (await import('@/modules/listen/ListeDetailPage')).ListeDetailPage })),
  training: lazy(async () => ({ default: (await import('@/modules/training/TrainingPage')).TrainingPage })),
  trainingPlan: lazy(async () => ({ default: (await import('@/modules/training/PlanPage')).PlanPage })),
  trainingEinheit: lazy(async () => ({ default: (await import('@/modules/training/EinheitPage')).EinheitPage })),
  trainingLive: lazy(async () => ({ default: (await import('@/modules/training/live/LivePage')).LivePage })),
  trainingAuswertung: lazy(async () => ({ default: (await import('@/modules/training/auswertung/AuswertungPage')).AuswertungPage })),
  ernaehrungHeute: lazy(async () => ({ default: (await import('@/modules/ernaehrung/HeutePage')).HeutePage })),
  ernaehrungVerlauf: lazy(async () => ({ default: (await import('@/modules/ernaehrung/VerlaufPage')).VerlaufPage })),
  ernaehrungAnalyse: lazy(async () => ({ default: (await import('@/modules/ernaehrung/AnalysePage')).AnalysePage })),
  ernaehrungPlan: lazy(async () => ({ default: (await import('@/modules/ernaehrung/PlanPage')).PlanPage })),
  ernaehrungRezepte: lazy(async () => ({ default: (await import('@/modules/ernaehrung/RezeptePage')).RezeptePage })),
  ernaehrungLebensmittel: lazy(async () => ({ default: (await import('@/modules/ernaehrung/LebensmittelPage')).LebensmittelPage })),
  trainingAusblick: lazy(async () => ({ default: (await import('@/modules/training/ausblick/AusblickPage')).AusblickPage })),
  trainingLaufen: lazy(async () => ({ default: (await import('@/modules/training/LaufenSeite')).LaufenSeite })),
  trainingKoerperdaten: lazy(async () => ({ default: (await import('@/modules/training/KoerperdatenSeite')).KoerperdatenSeite })),
  trainingUebungen: lazy(async () => ({ default: (await import('@/modules/training/UebungenPage')).UebungenPage })),
  wissenEintrag: lazy(async () => ({ default: (await import('@/modules/wissen/WissenDetailPage')).WissenDetailPage })),
  papierkorb: lazy(async () => ({ default: (await import('@/modules/papierkorb/PapierkorbPage')).PapierkorbPage })),
};

import { BereichRahmen, ZumBereich } from '@/modules/areas/BereichRahmen';

export const router = createHashRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        // Pfadlose Zwischenroute: ein Fehler in einer Seite erscheint IN der
        // Huelle - Seitenleiste und Navigation bleiben bedienbar.
        errorElement: <RouteFehler />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'bereiche', element: <lade.areas /> },
          { path: 'bereiche/:id', element: <lade.bereich /> },
          { path: 'einstellungen', element: <lade.settings /> },
          // Papierkorb (F-45) - erreichbar ueber die Einstellungen (Sicherung).
          { path: 'papierkorb', element: <lade.papierkorb /> },
          { path: 'kalender', element: <lade.calendar /> },
          { path: 'kalender/vorlagen', element: <lade.tagesvorlagen /> },
          { path: 'aufgaben', element: <lade.tasks /> },
          { path: 'listen', element: <lade.listen /> },
          { path: 'listen/:id', element: <lade.liste /> },
          { path: 'gewohnheiten', element: <lade.habits /> },
          // BE-03: Training und Ernaehrung stehen als Tabs im Rahmen von "Gesundheit & Koerper"; die Live-Seite bleibt Vollbild.
          {
            element: <BereichRahmen fest="gesundheit" />,
            children: [
            { path: 'training', element: <lade.training /> },
            { path: 'training/plan', element: <lade.trainingPlan /> },
            { path: 'training/einheit/:id', element: <lade.trainingEinheit /> },
            { path: 'training/uebungen', element: <lade.trainingUebungen /> },
            { path: 'training/auswertung', element: <lade.trainingAuswertung /> },
            { path: 'training/ausblick', element: <lade.trainingAusblick /> },
            { path: 'training/laufen', element: <lade.trainingLaufen /> },
            { path: 'training/koerperdaten', element: <lade.trainingKoerperdaten /> },
            { path: 'ernaehrung', element: <lade.ernaehrungHeute /> },
            { path: 'ernaehrung/plan', element: <lade.ernaehrungPlan /> },
            { path: 'ernaehrung/verlauf', element: <lade.ernaehrungVerlauf /> },
            { path: 'ernaehrung/analyse', element: <lade.ernaehrungAnalyse /> },
            { path: 'ernaehrung/rezepte', element: <lade.ernaehrungRezepte /> },
            { path: 'ernaehrung/lebensmittel', element: <lade.ernaehrungLebensmittel /> },
            ],
          },
          { path: 'training/live/:id', element: <lade.trainingLive /> },
          // BE-05: Finanzen als Bereich - Tabs im Rahmen des Bereichs; `/finanzen` fuehrt auf den Ueberblick.
          { path: 'finanzen', element: <ZumBereich fest="finanzen" sonst="/finanzen/buchungen" /> },
          {
            element: <BereichRahmen fest="finanzen" />,
            children: [
              { path: 'finanzen/buchungen', element: <lade.buchungenSeite /> },
              { path: 'finanzen/budget', element: <lade.budgetSeite /> },
              { path: 'finanzen/sparziele', element: <lade.sparzielSeite /> },
              { path: 'finanzen/abos', element: <lade.abosSeite /> },
              { path: 'finanzen/auswertung', element: <lade.auswertungSeite /> },
            ],
          },
          { path: 'projekte', element: <lade.projects /> },
          { path: 'projekte/:id', element: <lade.project /> },
          { path: 'ziele', element: <lade.goals /> },
          { path: 'ziele/:id', element: <lade.goal /> },
          // BE-04: Die Schulseite ist der Bereich "Schule" - ihre Tabs stehen im Rahmen des Bereichs; `/schule` fuehrt auf den Ueberblick.
          { path: 'schule', element: <ZumBereich fest="schule" sonst="/schule/faecher" /> },
          {
            element: <BereichRahmen fest="schule" />,
            children: [
              { path: 'schule/stundenplan', element: <lade.stundenplanSeite /> },
              { path: 'schule/faecher', element: <lade.faecherSeite /> },
              { path: 'schule/noten', element: <lade.notenSeite /> },
              { path: 'schule/klausuren', element: <lade.klausurenSeite /> },
              { path: 'schule/mitschriften', element: <lade.mitschriftenSeite /> },
              { path: 'schule/fach/:id', element: <lade.subject /> },
            ],
          },
          // Das Lebensrad hat keine eigene Seite mehr (V-07) - es steht in der Monatsreflexion.
          { path: 'lebensrad', element: <ZumLebensrad /> },
          { path: 'journal', element: <lade.journal /> },
          { path: 'rueckblick', element: <lade.review /> },
          { path: 'statistik', element: <lade.statistik /> },
          { path: 'ressourcen', element: <lade.resources /> },
          { path: 'wissen', element: <lade.wissen /> },
          { path: 'wissen/:id', element: <lade.wissenEintrag /> },
          // Frueher "Buecher" (V-13) - alte Lesezeichen fuehren weiter.
          { path: 'buecher', element: <Navigate to="/wissen" replace /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
